import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const EnquiryCartContext = createContext(null);
const STORAGE_KEY = "gbs_enquiry_cart_v1";

export const EnquiryCartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);

    // One-shot hydrate from localStorage on mount
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) setItems(JSON.parse(raw));
        } catch (err) {
            console.warn("[EnquiryCart] failed to read storage:", err);
        }
    }, []);

    // Persist whenever items change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (err) {
            console.warn("[EnquiryCart] failed to persist storage:", err);
        }
    }, [items]);

    const addItem = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const idx = prev.findIndex((p) => p.product_id === product.id);
            if (idx !== -1) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
                return next;
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    sku: product.sku,
                    image: product.images?.[0],
                    purity: product.purity,
                    weight_grams: product.weight_grams,
                    quantity: qty,
                    notes: "",
                },
            ];
        });
        setOpen(true);
    }, []);

    const removeItem = useCallback((productId) => {
        setItems((prev) => prev.filter((p) => p.product_id !== productId));
    }, []);

    const updateQty = useCallback((productId, qty) => {
        setItems((prev) => prev.map((p) => (p.product_id === productId ? { ...p, quantity: Math.max(1, qty) } : p)));
    }, []);

    const updateNotes = useCallback((productId, notes) => {
        setItems((prev) => prev.map((p) => (p.product_id === productId ? { ...p, notes } : p)));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

    const value = useMemo(
        () => ({ items, count, open, setOpen, addItem, removeItem, updateQty, updateNotes, clear }),
        [items, count, open, addItem, removeItem, updateQty, updateNotes, clear]
    );

    return <EnquiryCartContext.Provider value={value}>{children}</EnquiryCartContext.Provider>;
};

export const useEnquiryCart = () => {
    const ctx = useContext(EnquiryCartContext);
    if (!ctx) throw new Error("useEnquiryCart must be used inside EnquiryCartProvider");
    return ctx;
};
